import React, { useState } from 'react';
import { TrendingUp, Globe, Hand, Bot, Lock, Loader2 } from 'lucide-react';
import { PaperTradingPage } from '@/components/paper-trading/PaperTradingPage';
import { ForexPanel } from '@/components/paper-trading/ForexPanel';
import { forexService, forexLiveService } from '@/services/forexService';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { EquityManualPanel } from './EquityManualPanel';
import { LiveAutomationPanel } from './LiveAutomationPanel';
import { ForexAutomationPanel } from './ForexAutomationPanel';

type Asset = 'equity' | 'forex';
type View = 'manual' | 'automated';
type Env = 'paper' | 'live';

/**
 * Shared two-level trading shell used by BOTH Paper and Live Trading:
 *   Level 1 — asset tabs:  Equity | Forex
 *   Level 2 — view tabs:   Manual | Automated
 * so every (asset × view) cell is reachable and the two environments look
 * identical. Tab choices persist per-environment in localStorage.
 *
 * Cells without a backend surface yet (live automation, forex automation) show
 * a clear "coming" panel rather than an empty view — those land in later phases.
 */
export const TradingShell: React.FC<{ environment: Env }> = ({ environment }) => {
  const live = environment === 'live';
  // Validate persisted values against the allow-list — a clobbered/stale key
  // would otherwise pass an invalid string through and leave the body blank.
  const [asset, setAsset] = useState<Asset>(() => {
    const v = read(`${environment}.asset`, 'equity');
    return v === 'equity' || v === 'forex' ? v : 'equity';
  });
  const [view, setView] = useState<View>(() => {
    const v = read(`${environment}.view`, 'manual');
    return v === 'manual' || v === 'automated' ? v : 'manual';
  });

  const pickAsset = (a: Asset) => { setAsset(a); write(`${environment}.asset`, a); };
  const pickView = (v: View) => { setView(v); write(`${environment}.view`, v); };

  // Plan gate — live only. Gating here (before any panel mounts) means a user whose
  // plan lacks live trading fires NO live API calls at all, instead of collecting
  // raw 404s from the account/positions probes. Fails open when the plan can't be
  // read: the backend capability check on the order endpoints is authoritative.
  //
  // manageAnyway is the downgrade escape hatch: the backend deliberately gates
  // ENTRIES only, leaving account/positions/close/cancel open so a downgraded user
  // can flatten what they hold — so the UI must not wall those off completely.
  // Revealing the shell is safe: any entry attempt still 402s server-side.
  const { plan, limits, loading: planLoading } = usePlanLimits(live);
  const [manageAnyway, setManageAnyway] = useState(false);
  if (live && planLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (live && limits && !limits.liveTradingAllowed && !manageAnyway) {
    return <LiveUpgradePanel planName={plan?.name} onManagePositions={() => setManageAnyway(true)} />;
  }

  return (
    <div className="flex flex-col gap-3 h-full p-4">
      {live && (
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-red-500/10 flex items-center justify-center">
            <Globe className="h-4 w-4 text-red-500" />
          </div>
          <div>
            <h2 className="text-base font-semibold">Live Trading</h2>
            <span className="text-[11px] text-muted-foreground">Real-money — funded broker accounts</span>
          </div>
        </div>
      )}

      {/* Asset tabs */}
      <div className="flex items-center gap-1 border-b">
        <Tab icon={<TrendingUp className="h-4 w-4" />} label="Equity" subtitle="stocks + ETFs"
          active={asset === 'equity'} onClick={() => pickAsset('equity')} />
        <Tab icon={<Globe className="h-4 w-4" />} label="Forex" subtitle="FXCM · currency pairs"
          active={asset === 'forex'} onClick={() => pickAsset('forex')} />
      </div>

      {/* View sub-tabs */}
      <div className="flex items-center gap-1">
        <Pill icon={<Hand className="h-3.5 w-3.5" />} label="Manual" active={view === 'manual'} onClick={() => pickView('manual')} />
        <Pill icon={<Bot className="h-3.5 w-3.5" />} label="Automated" active={view === 'automated'} onClick={() => pickView('automated')} />
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto">
        {asset === 'equity' && view === 'manual' && <EquityManualPanel env={environment} />}
        {asset === 'equity' && view === 'automated' && (
          live
            ? <LiveAutomationPanel assetClass="equity" />
            : <PaperTradingPage />
        )}
        {asset === 'forex' && view === 'manual' && (
          <ForexPanel svc={live ? forexLiveService : forexService} live={live} />
        )}
        {asset === 'forex' && view === 'automated' && (
          live
            ? <LiveAutomationPanel assetClass="forex" />
            : <ForexAutomationPanel />
        )}
      </div>
    </div>
  );
};

/** What a user whose plan lacks live trading sees on the Live Trading tab. The tab
 * itself stays visible (hiding it would remove the upgrade path); this panel is the
 * conversion surface. Paper trading is unaffected and said so explicitly. The
 * onManagePositions link exists for the downgraded-with-open-positions case — the
 * plan gate blocks new entries, never managing or flattening existing exposure. */
function LiveUpgradePanel({ planName, onManagePositions }: { planName?: string; onManagePositions?: () => void }) {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-md w-full rounded-lg border bg-card p-6 text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
          <Lock className="h-6 w-6 text-amber-500" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Live trading is a Pro feature</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Live (real-money) trading isn't included on
            {planName ? <> the <span className="font-medium text-foreground">{planName}</span> plan</> : ' your plan'}.
            Paper trading stays fully available — build and prove your strategies there,
            then upgrade to connect your funded broker accounts.
          </p>
        </div>
        <div className="flex items-center justify-center gap-2">
          <a href="#/dashboard/configuration/billing"
            className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
            View plans & upgrade
          </a>
          <a href="#/dashboard/paper-trading"
            className="inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent transition-colors">
            Go to paper trading
          </a>
        </div>
        {onManagePositions && (
          <button type="button" onClick={onManagePositions}
            className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors">
            Downgraded with open live positions? View &amp; close them
          </button>
        )}
      </div>
    </div>
  );
}

function Tab({ icon, label, subtitle, active, onClick }: {
  icon: React.ReactNode; label: string; subtitle: string; active: boolean; onClick: () => void;
}) {
  return (
    <button onClick={onClick} role="tab" aria-selected={active}
      className={[
        'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors',
        active ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
      ].join(' ')}>
      {icon}
      <div className="flex flex-col items-start leading-tight">
        <span>{label}</span>
        <span className="text-[10px] font-normal text-muted-foreground">{subtitle}</span>
      </div>
    </button>
  );
}

function Pill({ icon, label, active, onClick }: { icon: React.ReactNode; label: string; active: boolean; onClick: () => void; }) {
  return (
    <button onClick={onClick}
      className={[
        'flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
        active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground',
      ].join(' ')}>
      {icon}{label}
    </button>
  );
}

function read(key: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  try { return window.localStorage.getItem(`trading.${key}`) ?? fallback; } catch { return fallback; }
}
function write(key: string, value: string) {
  try { window.localStorage.setItem(`trading.${key}`, value); } catch { /* private mode */ }
}

export default TradingShell;
